import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { FrequentFlyerItemType } from '~/components/LeBusinessTraveller/pages/BusinessDetailsPage/Tabs/FrequentFlyerTab';

import {
  EmployeeLoyaltyPrograms,
  EmployeeLoyaltyProgramsRequest,
  getEmployee,
  getEmployeeLoyaltyPrograms,
  getFrequentFlyerList,
  updateEmployeeLoyaltyPrograms,
} from '~/services/BusinessTraveller/BusinessTravellerService';

import LoyaltyProgramSection from './LoyaltyProgramSection';
import {
  LoyaltyProgramRow,
  LoyaltyProgramRowsState,
  frequentFlyerProgramsConfig,
  hotelLoyaltyProgramsConfig,
} from './loyaltyProgramsConfig';
import { computeFinalLoyaltyRows, initialiseLoyaltyProgramRows } from './utils';

// Airline name mapping from business frequent flyer system
const AirlineNameMap: { [key: string]: string } = {
  qantas: 'Qantas Business Rewards',
  virgin: 'Virgin Australia Business Flyer',
};

interface Props {
  user: App.User;
}

export default function FrequentFlyerMembershipsTab({ user }: Props) {
  const [flightProgramsState, setFlightProgramsState] = useState<LoyaltyProgramRowsState>({
    initialRows: [],
    currentRows: [],
  });
  const [hotelProgramsState, setHotelProgramsState] = useState<LoyaltyProgramRowsState>({
    initialRows: [],
    currentRows: [],
  });
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [corporateAgreements, setCorporateAgreements] = useState<Array<FrequentFlyerItemType>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Callback function to load loyalty programs data
  const loadLoyaltyProgramsData = useCallback(
    async (forceBusinessId?: string) => {
      try {
        let currentBusinessId = forceBusinessId || businessId;

        // If no businessId provided, fetch employee data first
        if (!currentBusinessId) {
          const employee = await getEmployee(user.id_member);

          if (!employee?.businessId) {
            setError('This user is not associated with a business');
            return;
          }

          currentBusinessId = employee.businessId;
          setBusinessId(currentBusinessId);

          // Load corporate agreements only on initial load
          const businessFrequentFlyerData = await getFrequentFlyerList(currentBusinessId);
          if (Array.isArray(businessFrequentFlyerData?.result)) {
            setCorporateAgreements(businessFrequentFlyerData.result);
          }
        }

        // Fetch loyalty programs data
        const loyaltyProgramsData = await getEmployeeLoyaltyPrograms(currentBusinessId, user.id_member);

        if (loyaltyProgramsData?.result && Array.isArray(loyaltyProgramsData.result)) {
          // Separate flight and hotel programs
          const flightPrograms: Array<LoyaltyProgramRow> = loyaltyProgramsData.result
            .filter(
              (lp: EmployeeLoyaltyPrograms) =>
                lp.type === 'flight' && frequentFlyerProgramsConfig[lp.program] && lp.memberNumber,
            )
            .map((lp: EmployeeLoyaltyPrograms) => ({
              id: crypto.randomUUID(),
              program: lp.program,
              memberNumber: lp.memberNumber || '',
              type: 'flight',
            }));

          const hotelPrograms: Array<LoyaltyProgramRow> = loyaltyProgramsData.result
            .filter(
              (lp: EmployeeLoyaltyPrograms) =>
                lp.type === 'hotel' && hotelLoyaltyProgramsConfig[lp.program] && lp.memberNumber,
            )
            .map((lp: EmployeeLoyaltyPrograms) => ({
              id: crypto.randomUUID(),
              program: lp.program,
              memberNumber: lp.memberNumber || '',
              type: 'hotel',
            }));

          // Set rows - if no existing data, create one empty row per section
          setFlightProgramsState({
            initialRows: initialiseLoyaltyProgramRows(flightPrograms, 'flight'),
            currentRows: initialiseLoyaltyProgramRows(flightPrograms, 'flight'),
          });
          setHotelProgramsState({
            initialRows: initialiseLoyaltyProgramRows(hotelPrograms, 'hotel'),
            currentRows: initialiseLoyaltyProgramRows(hotelPrograms, 'hotel'),
          });
        } else {
          // No data from API, initialize with empty rows
          setFlightProgramsState({
            initialRows: initialiseLoyaltyProgramRows([], 'flight'),
            currentRows: initialiseLoyaltyProgramRows([], 'flight'),
          });
          setHotelProgramsState({
            initialRows: initialiseLoyaltyProgramRows([], 'hotel'),
            currentRows: initialiseLoyaltyProgramRows([], 'hotel'),
          });
        }
      } catch (err) {
        // The error thrown here will be caught and set the Error state
        throw Error(err.message || 'Failed to load loyalty programs data');
      }
    },
    [businessId, user.id_member],
  );

  useEffect(() => {
    const loadExistingData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        await loadLoyaltyProgramsData();
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingData();
  }, [user.id_member, loadLoyaltyProgramsData]);

  // Wrapper function to update only currentRows based on program type
  const handleRowsChange = useCallback((programType: 'flight' | 'hotel', newRows: Array<LoyaltyProgramRow>) => {
    if (programType === 'flight') {
      setFlightProgramsState((prev) => ({
        ...prev,
        currentRows: newRows,
      }));
    } else {
      setHotelProgramsState((prev) => ({
        ...prev,
        currentRows: newRows,
      }));
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      if (!businessId) {
        setError('Business ID not available for this user');
        return;
      }

      const loyaltyProgramsData = computeFinalLoyaltyRows(flightProgramsState, hotelProgramsState);

      const payload: EmployeeLoyaltyProgramsRequest = {
        employeeLoyaltyPrograms: loyaltyProgramsData,
      };

      await updateEmployeeLoyaltyPrograms(businessId, payload);
      // Refetch the latest data after successful submission
      await loadLoyaltyProgramsData(businessId);

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to update loyalty programs');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading loyalty programs data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Loyalty programs updated successfully.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {corporateAgreements.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Corporate Memberships & Fare Modifier Codes
          </Typography>
          <Stack spacing={2}>
            {corporateAgreements.map((agreement, index) => (
              <Box key={agreement.id || index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {AirlineNameMap[agreement.airlineName] || agreement.airlineName}
                  </Typography>
                  {agreement.verified && <Chip label="Verified" color="success" size="small" />}
                  {agreement.isCorporateAgreement && <Chip label="Corporate" color="primary" size="small" />}
                </Box>
                <Typography variant="body2">
                  Fare Modifier Code:{' '}
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>
                    {agreement.code}
                  </Typography>
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={4}>
          <LoyaltyProgramSection
            title="Frequent Flyer Programs"
            programType="flight"
            programConfigMap={frequentFlyerProgramsConfig}
            rows={flightProgramsState.currentRows}
            onRowsChange={(newRows) => handleRowsChange('flight', newRows)}
            isSubmitting={isSubmitting}
          />

          <LoyaltyProgramSection
            title="Hotel Loyalty Programs"
            programType="hotel"
            programConfigMap={hotelLoyaltyProgramsConfig}
            rows={hotelProgramsState.currentRows}
            onRowsChange={(newRows) => handleRowsChange('hotel', newRows)}
            isSubmitting={isSubmitting}
          />

          <Box>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Memberships'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
